import React, { FC, useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Form, Input } from 'antd';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/ru_RU';
import ReactInputMask, { Props as InputMaskProps } from 'react-input-mask';
import {
  getVisibleImages,
  normalizePhone,
  phoneFormatter,
  priceFormatter,
  renderRequiredMark,
  rules,
  validatePhone,
} from 'common/helpers';
import { Image } from 'common/components';
import { deliveryTimeFormatter } from 'common/helpers/formatter.helper';
import { PHONE_MASK, PHONE_PATTERN_WITH_CODE } from 'common/config';
import { ArrowLeftShort, ArrowRightShort, Calendar, Copy } from 'common/svg';
import { useAppSelector } from 'app/store/store.hooks';
import { DeliveryAddressSelector } from 'entities/Address/components';
import { EDeliveryType, IBasket, IBasketDelivery, IBasketGoods, IUpdateBasketPayload } from 'entities/Basket/Basket.models';
import {
  getBasketDeliveryType,
  getIsDeliveryDateUnavailable,
  isDateBeforeDeliveryStartDate,
} from 'entities/Basket/Basket.helper';
import { UserAssigneeSelector } from 'entities/User/components';
import { getUserAssigneeOptions } from 'entities/User/User.helper';
import { selectSubdivision } from 'entities/Subdivision/Subdivision.slice';
import { selectUserAssignees, selectUserList } from 'entities/User/User.slice';
import { IAddress } from 'entities/Address/Address.models';

const InputMask = ReactInputMask as React.ComponentType<InputMaskProps>;

interface IComponentProps {
  basket: IBasket;
  selectedGoods: IBasketGoods[];
  delivery: IBasketDelivery;
  onAddressChange: (address: IAddress) => void;
  onSubmit: (payload: Omit<IUpdateBasketPayload, 'id'>) => void;
}

export const DeliveryMethod: FC<IComponentProps> = ({ basket, selectedGoods, delivery, onAddressChange, onSubmit }) => {
  const subdivision = useAppSelector(selectSubdivision);
  const userList = useAppSelector(selectUserList);
  const userAssignees = useAppSelector(selectUserAssignees);

  const addresses = subdivision?.addresses ?? [];
  const { isDelivery } = getBasketDeliveryType(basket);

  const [payload, setPayload] = useState<Omit<IUpdateBasketPayload, 'id'>>({ deliveryType: EDeliveryType.Delivery });
  const [form] = Form.useForm();
  const phone = Form.useWatch('phone', form);
  const addressId = Form.useWatch('addressId', form);

  const assignees = useMemo(() => getUserAssigneeOptions(userList, userAssignees, ''), [userList, userAssignees]);

  useEffect(() => {
    const addressExists = addresses.find((item) => item.id === basket.addressId);

    form.setFieldsValue({ addressId: addressExists ? basket.addressId : undefined });

    if (isDelivery) {
      const assigneeExists = assignees.find((item) => item.label === basket.assignee);

      form.setFieldsValue({
        ...basket,
        deliveryDate: getIsDeliveryDateUnavailable(delivery.deliveryInDays, basket.deliveryDate)
          ? null
          : dayjs(basket.deliveryDate),
        addressId: addressExists ? basket.addressId : undefined,
        assignee: assigneeExists ? assigneeExists.value : undefined,
        phone: phoneFormatter(basket.phone),
      });
    }
  }, [basket, delivery.deliveryInDays]);

  const { visibleImages, hiddenImagesCount } = useMemo(() => getVisibleImages(selectedGoods), [selectedGoods]);
  const selectedAddress = useMemo(() => addresses.find((i) => i.id === addressId), [addresses, addressId]);
  const deliveryDisabledDate = useMemo(() => {
    if (delivery.deliveryInDays === undefined) {
      return () => true;
    }

    return (current: dayjs.Dayjs) => current && isDateBeforeDeliveryStartDate(current, delivery.deliveryInDays as number);
  }, [delivery.deliveryInDays]);
  const isValidPhone = validatePhone(phone);
  // Пикер залочен и когда срок неизвестен, и когда бакет = «Доступно под заказ» (null).
  const isDeliveryDatePickerDisabled =
    delivery.deliveryInDays === undefined || deliveryTimeFormatter.bucketDays(delivery.deliveryInDays) === null;

  const copyPhone = (e: React.MouseEvent): void => {
    e.stopPropagation();
    const phone = form.getFieldValue('phone');

    navigator.clipboard.writeText(`${phone}`);
  };

  const changeForm = (values: Omit<IUpdateBasketPayload, 'id'>): void => {
    setPayload((prev) => ({ ...prev, ...values }));
  };

  const handleSubmit = () => {
    const normalizedAssignee = payload.assignee ? payload.assignee.split('_').slice(1).join(' ') : undefined;
    const normalizedPhone = payload.phone ? normalizePhone(payload.phone) : undefined;
    let deliveryDate = undefined;

    if (payload.deliveryDate) {
      deliveryDate = dayjs(payload.deliveryDate).format('YYYY-MM-DD');
    }

    if (isDeliveryDatePickerDisabled) {
      deliveryDate = null;
    }

    onSubmit({
      ...payload,
      deliveryDate,
      assignee: normalizedAssignee,
      phone: normalizedPhone,
    });
  };

  return (
    <div>
      <div className="mb-6 p-4 flex align-center justify-between delivery-method__option-good">
        <div>
          <div className="ff-regular fs-s color-dark-grey">{`Вес заказа ${delivery.totalWeight} кг`}</div>
          <span className="ff-medium fs-s">{`Стоимость доставки: ${priceFormatter(delivery.deliveryPriceWithTaxes)}`}</span>
          <div className="mt-2 ff-regular fs-xs color-dark-grey">
            Указанная стоимость является ориентировочной. Точную цену сообщит менеджер при подтверждении заказа.
          </div>
        </div>

        <div className="flex align-center gap-3">
          {visibleImages.map((src, index) => (
            <Image key={index} size={44} src={src} />
          ))}

          {!!hiddenImagesCount && <div className="ff-medium fs-xs">{`+${hiddenImagesCount}`}</div>}
        </div>
      </div>

      <div className="overflow-y-scroll scrollbar-none" style={{ height: 374 }}>
        <Form
          form={form}
          requiredMark={renderRequiredMark}
          layout="vertical"
          scrollToFirstError={{ behavior: 'instant', block: 'end' }}
          onValuesChange={changeForm}
          onFinish={handleSubmit}
        >
          <Form.Item
            className="mb-6"
            label="Срок доставки"
            name="deliveryDate"
            rules={isDeliveryDatePickerDisabled ? undefined : [rules.required()]}
          >
            <DatePicker
              placeholder={isDeliveryDatePickerDisabled ? 'Менеджер уточнит дату поставки' : 'Выберите дату доставки'}
              suffixIcon={<Calendar className="icon-dark-grey" />}
              allowClear={false}
              showToday={false}
              inputReadOnly
              locale={locale}
              format="DD.MM.YYYY (dd)"
              disabledDate={deliveryDisabledDate}
              prevIcon={<ArrowLeftShort />}
              nextIcon={<ArrowRightShort />}
              disabled={isDeliveryDatePickerDisabled}
            />
          </Form.Item>

          <Form.Item
            className="mb-6"
            label="Адрес доставки"
            name="addressId"
            rules={[rules.required()]}
            extra={
              selectedAddress ? (
                <span className="ff-regular fs-xs">
                  Перед оформлением проверьте сроки и стоимость доставки из города
                  <span className="color-bright-green"> {selectedAddress.city.name}</span>.
                </span>
              ) : null
            }
          >
            <DeliveryAddressSelector onAddressChange={onAddressChange} />
          </Form.Item>

          <div className="mb-6 flex align-center gap-3">
            <Form.Item label="Контактное лицо" name="assignee" rules={[rules.required()]}>
              <UserAssigneeSelector />
            </Form.Item>

            <Form.Item
              className="flex-1"
              label="Телефон"
              name="phone"
              rules={[rules.required(), rules.pattern(PHONE_PATTERN_WITH_CODE, 'Неверный формат телефона')]}
            >
              <InputMask mask={PHONE_MASK}>
                {(inputProps: object) => (
                  <Input
                    {...inputProps}
                    placeholder="+7 (___) ___-__-__"
                    suffix={
                      <Copy
                        className="icon-dark-grey cursor-pointer"
                        style={{
                          pointerEvents: isValidPhone ? 'auto' : 'none',
                          opacity: isValidPhone ? 1 : 0,
                        }}
                        onClick={copyPhone}
                      />
                    }
                  />
                )}
              </InputMask>
            </Form.Item>
          </div>

          <Form.Item className="mb-0" label="Комментарий к заказу" name="comment">
            <Input.TextArea
              className="textarea"
              placeholder="Оставьте ваш комментариий"
              maxLength={255}
              style={{ minHeight: 120 }}
            />
          </Form.Item>
        </Form>
      </div>

      <div className="mt-6">
        <div className="mb-5">
          <Button className="button-lg primary w-full" style={{ minHeight: 52 }} onClick={form.submit}>
            Привезти сюда
          </Button>
        </div>

        <span className="ff-regular fs-s color-dark-grey" style={{ letterSpacing: 0.1 }}>
          После оформления заказа менеджер свяжется с вами для подтверждения даты и времени доставки
        </span>
      </div>
    </div>
  );
};
